import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useLoyaltySettingMutations } from '~/hooks/use-loyalty-settings';
import { settingSchema, type SettingFormValues } from '~/lib/schemas/setting';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: SettingFormValues | null;
}

/**
 * Edit a loyalty-points setting (Παράμετρος): its value and description. The name is read-only, as the platform reads
 * these settings by name (renaming LoyaltyPoints_Active used to turn loyalty off);
 * the site override (wsid) and param group (prmg = LoyaltyPoints) are carried through unchanged so
 * an edit never re-scopes the setting (legacy onDataGridRowUpdatingMergeUnchanged parity).
 */
export function LoyaltySettingEditDialog({ open, onOpenChange, initial }: Props) {
	const { t } = useTranslation();
	const { update } = useLoyaltySettingMutations();
	const form = useForm<SettingFormValues>({
		resolver: zodResolver(settingSchema) as Resolver<SettingFormValues>,
		defaultValues: initial ?? undefined,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, formState } = form;

	useEffect(() => {
		if (open && initial) reset(initial);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			await update.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t('loyalty.overview.editTitle')}</DialogTitle>
					<DialogDescription>{t('loyalty.overview.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<FormField label={t('loyalty.overview.columns.name')} error={formState.errors.pn?.message}>
						<Input {...register('pn')} readOnly className="bg-muted" />
					</FormField>
					<FormField label={t('loyalty.overview.columns.value')} error={formState.errors.pv?.message}>
						<Input {...register('pv')} maxLength={100} aria-invalid={Boolean(formState.errors.pv)} autoFocus />
					</FormField>
					<FormField label={t('loyalty.overview.columns.description')}>
						<Textarea rows={2} {...register('descr')} />
					</FormField>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={update.isPending}>
							{update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
